import { makeVar, useReactiveVar } from '@apollo/client';
import deepEqual from 'react-fast-compare';

import type Language from '../../../types/Language';
import type { Configuration } from '../../../types/Configuration';
import findDefaultLanguage from './findDefaultLanguage';
import { fetchTextkeys } from '../translation/useTranslation';

const initialState: Language = {
  code: '',
  label: 'common.language.unknown',
};

const currentLanguage = makeVar<Language>(initialState);

/**
 * Should be used directly only for tests purposes. Use the hook otherwise
 */
export const resetCurrentLanguage = () => currentLanguage(initialState);

/**
 * Should be used directly only for tests purposes. Use the hook otherwise
 */
export const setCurrentLanguage = (language: Language) => {
  const current = currentLanguage();
  if (!deepEqual(current, language)) {
    currentLanguage(language);
  }
};

const changeLanguageAndTranslation = (language: Language, pid: string, requestId?: string, channelSource?: string) => {
  setCurrentLanguage(language);
  fetchTextkeys(pid, language.code, requestId, channelSource).catch(console.error);
};

const initializeCurrentLanguage = (config: Configuration, pid: string, requestId?: string, channelSource?: string) => {
  const initialized = currentLanguage().code !== '';

  const defaultLanguage = findDefaultLanguage(config);

  if (!initialized && defaultLanguage) {
    changeLanguageAndTranslation(defaultLanguage, pid, requestId, channelSource);
  }
};

const useCurrentLanguage = () => {
  const reactiveCurrentLanguage = useReactiveVar(currentLanguage);

  return {
    actions: {
      initializeCurrentLanguage,
      changeLanguageAndTranslation,
    },
    states: {
      currentLanguage: reactiveCurrentLanguage,
    },
  };
};

export default useCurrentLanguage;
